import React, { ChangeEvent, Component } from 'react';
import styled from 'styled-components';
import Honeybadger from '@honeybadger-io/js';
import {
  utils,
  SignUpProps,
  SignUpError,
  graphql,
  LabsResult,
  Lab,
  Patient,
  GoogleAuthError,
  DropdownOption
} from 'kb-shared';
import TextInput from '../../../components/TextInput/NewTextInput';
import Button from '../../../components/Button/NewButton';
import SelectInput, { SelectWithOptions } from '../../../components/SelectInput';
import { Query } from 'react-apollo';

const {
  query: { LABS }
} = graphql;

const {
  GENDER_INPUT_DEFAULTS,
  PRONOUN_INPUT_DEFAULTS,
  COUNTRIES_OPTIONS,
  US_COUNTRY_OPTION,
  isDOBValid,
  isDateValid,
  isPhoneNumberValid,
  isLabValid,
  isInputValid,
  states
} = utils;

const InputContainer = styled.div`
  max-width: 400px;
  flex: 1;
`;

const TwoFieldWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const VMarginWrapper = styled.div`
  margin: 30px 0;
`;

const InputWrapperMargin = styled.div`
  flex: 1;
  :first-child {
    margin-right: 8px;
  }
  :last-child {
    margin-left: 8px;
  }
`;

const GenderInputWrapper = styled(InputWrapperMargin)`
  min-width: 110px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: 40px;
  padding-bottom: 40px;
`;

type Props = {
  preselectedLab: Lab | null;
  patient: Patient;
  isLoggedIn: boolean;
  userConfirmationState: string | null;
  signUpUser: (props: SignUpProps) => void;
  onSuccess: () => void;
  onError: (arg0: string) => void;
  loading: boolean;
  signUpError: SignUpError | null;
  googleAuthError: GoogleAuthError | null;
};

interface Error {
  email: boolean;
  password: boolean;
  firstName: boolean;
  lastName: boolean;
  gender: boolean;
  birthdate: boolean;
  dob: boolean;
  phone: boolean;
  patientLab: boolean;
  membershipTerms: boolean;
  country: boolean;
  addressLine: boolean;
  city: boolean;
  state: boolean;
  zipCode: boolean;
  referralSource: boolean;
}

type State = {
  gender: string;
  genderOption: DropdownOption | null;
  pronoun: string;
  pronounOption: DropdownOption | null;
  birthdate: string;
  phone: string;
  errors: Partial<Error>;
  country: string;
  countryOption: DropdownOption;
  patientLab: Lab | null;
  hasDisplayedError: boolean;
  addressLine: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  referralSource: string;
};

export default class SignUpWithEmail extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      gender: GENDER_INPUT_DEFAULTS[1].value, // female
      genderOption: GENDER_INPUT_DEFAULTS[1], // { label: 'female', value: 'female' },
      pronoun: '',
      pronounOption: { label: '', value: '' },
      birthdate: '',
      phone: '',
      errors: {},
      patientLab: props.preselectedLab,
      hasDisplayedError: false,
      addressLine: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: US_COUNTRY_OPTION.value,
      countryOption: US_COUNTRY_OPTION,
      referralSource: ''
    };
  }

  sortedLabs = [];

  componentDidMount() {
    if (window.analytics) {
      window.analytics.page('Google Sign Up');
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { isLoggedIn } = this.props;
    if (isLoggedIn && isLoggedIn !== prevProps.isLoggedIn) {
      this.props.onSuccess();
    }
  }

  UNSAFE_componentWillReceiveProps(newProps: Props) {
    const { onError, signUpError, googleAuthError } = newProps;
    const { hasDisplayedError } = this.state;

    if (hasDisplayedError) return;

    if (googleAuthError && googleAuthError.type === 'UserAlreadyExistsAsEmail') {
      onError(
        'Sorry, we cannot log you in that way. If you already have an account, please log-in using your email address and password.'
      );
      this.setState({ hasDisplayedError: true });
    }

    if (signUpError) {
      const onError = this.props.onError;
      switch (signUpError.type) {
        case 'Unknown':
        default:
          onError('Sorry, we were unable to create your account. Please try again.');
          Honeybadger.notify(signUpError, 'SignUpError');
          this.setState({ hasDisplayedError: true });
          break;
      }
    }
  }

  updatePatientLab = (value: Lab) => {
    this.setState({
      patientLab: value
    });
  };

  updateReferralSource = (value: DropdownOption) => {
    this.setState({ referralSource: value.value });
  };

  onUpdateInputText = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      [event.target.name]: event.target.value
    });
  };

  updateGender = (option: DropdownOption) => {
    this.setState({
      gender: option.value,
      genderOption: option
    });
  };

  updatePronoun = (option: DropdownOption) => {
    this.setState({
      pronoun: option.value,
      pronounOption: option
    });
  };

  updateStateOption = (option: DropdownOption) => {
    this.setState({
      state: option.value
    });
  };

  onUpdateCountry = (option, action) => {
    this.setState({
      [action.name]: option.value,
      [`${[action.name]}Option`]: option
    });

    if (this.isUS(option.value)) {
      return this.updatePatientLab('');
    }

    const labOther = this.sortedLabs.find(item => item.name === 'Other');
    this.updatePatientLab(labOther);
  };

  alphabetically = () => {
    // @ts-ignore
    return function(a, b) {
      // equal items sort equally
      if (a.name === b.name) {
        return 0;
      }
      // Other sort after anything else
      else if (a.name === 'Other') {
        return 1;
      } else if (b.name === 'Other') {
        return -1;
      }
      // otherwise lowest sorts first
      else {
        return a.name < b.name ? -1 : 1;
      }
    };
  };

  async _handleSignup() {
    const {
      onError,
      patient: { firstName, lastName, email }
    } = this.props;

    const fieldErrors = this._getFieldErrors();
    if (fieldErrors) {
      const { errorStrings, errors } = fieldErrors;

      const somethingWentWrongString = 'Something went wrong!';
      let errorMessage = somethingWentWrongString;
      if (errorStrings.length > 0) {
        if (errors.dob) {
          errorMessage = 'Patients must be 18 years or older to schedule a consult.';
        }

        if (errors.phone) {
          errorMessage = 'You must provide a phone number.';
        }

        if (errors.birthdate) {
          errorMessage = 'Please enter a valid date.';
        }

        if (errors.gender) {
          errorMessage = 'You must select a gender.';
        }

        if (errors.patientLab) {
          errorMessage = 'You must select a location.';
        }

        if (errors.addressLine) {
          errorMessage = 'Please enter an address line';
        }

        if (errors.city) {
          errorMessage = 'Please enter a city name';
        }

        if (errors.state) {
          errorMessage = 'Please select a state';
        }

        if (errors.zipCode) {
          errorMessage = 'Please enter a Zip Code';
        }

        if (errors.referralSource) {
          errorMessage = 'Please let us know how you heard about us';
        }
      } else {
        errorMessage = errorStrings.reduce((agr, error, index, array) => {
          const message = `${agr} ${error}`;
          if (index + 1 !== array.length && array.length > 1) {
            return `${message}, `;
          }
          return message;
        }, 'Please fix the following fields:');
      }
      this.setState(() => ({ errors }));
      onError(errorMessage);

      if (errorMessage === somethingWentWrongString) {
        Honeybadger.notify('Something went wrong shown to the user', 'SignUpError');
      }

      return;
    }
    // TODO: Better handle this - should never get here due to above validation.
    // This silences the potential flow error below though that patientLab may be null
    if (!this.state.patientLab) {
      return;
    }

    const {
      gender,
      pronoun,
      birthdate,
      phone,
      patientLab,
      addressLine,
      addressLine2,
      city,
      state,
      zipCode,
      country,
      referralSource
    } = this.state;

    const signUpProps: SignUpProps = {
      firstName,
      lastName,
      email,
      phone,
      gender: gender,
      pronoun: pronoun,
      birthdate: birthdate,
      referralSource,
      labId: parseInt(patientLab.id),
      address: {
        address1: addressLine,
        address2: addressLine2,
        city: city,
        state: state,
        zipcode: zipCode,
        country: country
      }
    };

    this.props.signUpUser(signUpProps);
  }

  isEmptyString = (str: string) => str.match(/^ *$/) !== null;

  _getFieldErrors = () => {
    const {
      gender,
      birthdate,
      patientLab,
      phone,
      addressLine,
      city,
      state,
      zipCode,
      country,
      referralSource
    } = this.state;

    const errorStrings = [];
    const errors: Partial<Error> = {};

    if (!isInputValid(gender)) {
      errorStrings.push('Gender');
      errors.gender = true;
    }
    if (!isDOBValid(birthdate)) {
      errorStrings.push('Birthdate');
      errors.dob = true;
    }
    if (!isDateValid(birthdate)) {
      errorStrings.push('Birthdate');
      errors.birthdate = true;
    }
    if (this.isUS(country) && !isPhoneNumberValid(phone)) {
      errorStrings.push('Phone number');
      errors.phone = true;
    }
    if (!this.isUS(country) && phone.length <= 1) {
      errorStrings.push('Phone number');
      errors.phone = true;
    }
    // @ts-ignore
    if (!isLabValid(patientLab)) {
      errorStrings.push('Kindbody Home Location');
      errors.patientLab = true;
    }
    if (this.isEmptyString(addressLine)) {
      errorStrings.push('Address Line');
      errors.addressLine = true;
    }
    if (this.isEmptyString(city)) {
      errorStrings.push('City');
      errors.city = true;
    }
    if (this.isUS(country) && this.isEmptyString(state)) {
      errorStrings.push('State');
      errors.state = true;
    }
    if (this.isUS(country) && this.isEmptyString(zipCode)) {
      errorStrings.push('Zip Code');
      errors.zipCode = true;
    }
    if (this.isEmptyString(referralSource)) {
      errorStrings.push('Referral Source');
      errors.referralSource = true;
    }

    if (errorStrings.length === 0 && Object.keys(errors).length === 0) return false;

    return {
      errors,
      errorStrings
    };
  };

  isUS = (country: string) => country === 'United States' || country === 'USA';

  stateOpt = () => states.find(s => s.value === this.state.state);

  render() {
    const {
      genderOption,
      pronounOption,
      birthdate,
      errors,
      patientLab,
      phone,
      addressLine,
      addressLine2,
      city,
      zipCode,
      country,
      countryOption,
      state,
      referralSource
    } = this.state;
    return (
      <InputContainer>
        <VMarginWrapper>
          <SelectInput
            name="country"
            label="COUNTRY"
            extractTitle={item => (item ? item.label : '')}
            value={country}
            placeholder="Select country"
            selectedOption={countryOption}
            onSelect={this.onUpdateCountry}
            options={COUNTRIES_OPTIONS}
          />
        </VMarginWrapper>

        <TwoFieldWrapper>
          <InputWrapperMargin>
            <TextInput
              name="addressLine"
              label="ADDRESS LINE 1"
              value={addressLine}
              onChange={this.onUpdateInputText}
              placeholder="Address"
              error={errors.addressLine}
            />
          </InputWrapperMargin>
          <InputWrapperMargin>
            <TextInput
              name="addressLine2"
              label="ADDRESS LINE 2"
              value={addressLine2}
              onChange={this.onUpdateInputText}
              placeholder={'Address'}
              error={errors.addressLine}
            />
          </InputWrapperMargin>
        </TwoFieldWrapper>

        {this.isUS(country) ? (
          <VMarginWrapper>
            <SelectInput
              label="STATE"
              type="select"
              placeholder="Select state"
              selectedOption={this.stateOpt()}
              extractTitle={item => (item ? item.label : '')}
              options={states}
              onSelect={option => this.updateStateOption(option)}
            />
          </VMarginWrapper>
        ) : (
          <VMarginWrapper>
            <TextInput
              name="state"
              label="STATE"
              value={state}
              placeholder="State"
              // @ts-ignore
              onChange={this.onUpdateInputText}
              error={errors.state}
            />
          </VMarginWrapper>
        )}

        <TwoFieldWrapper>
          <InputWrapperMargin>
            <TextInput
              name="city"
              label="CITY"
              placeholder="City"
              value={city}
              onChange={this.onUpdateInputText}
              error={errors.city}
            />
          </InputWrapperMargin>

          {this.isUS(country) ? (
            <InputWrapperMargin>
              <TextInput
                name="zipCode"
                label="ZIP"
                value={zipCode}
                inputType="zip"
                placeholder="55555"
                autoComplete="postal-code"
                onChange={this.onUpdateInputText}
                error={errors.zipCode}
              />
            </InputWrapperMargin>
          ) : (
            <InputWrapperMargin>
              <TextInput
                name="zipCode"
                label="ZIP"
                value={zipCode}
                placeholder="ZIP"
                onChange={this.onUpdateInputText}
                error={errors.zipCode}
              />
            </InputWrapperMargin>
          )}
        </TwoFieldWrapper>

        <VMarginWrapper>
          <TwoFieldWrapper>
            <InputWrapperMargin>
              <TextInput
                inputType={this.isUS(country) ? 'phone' : 'text'}
                name="phone"
                label="PHONE NUMBER"
                value={phone}
                onChange={this.onUpdateInputText}
                error={errors.phone}
                autoComplete="tel"
                inputMode="tel"
                placeholder="(+XXXX)Phone Number"
              />
            </InputWrapperMargin>
            <InputWrapperMargin>
              <TextInput
                inputType="date"
                name="birthdate"
                label="BIRTHDATE"
                value={birthdate}
                onChange={this.onUpdateInputText}
                error={errors.birthdate || errors.dob}
                autoComplete="bday"
              />
            </InputWrapperMargin>
          </TwoFieldWrapper>
        </VMarginWrapper>

        <VMarginWrapper>
          <TwoFieldWrapper>
            <GenderInputWrapper>
              <SelectInput
                label="SEX AT BIRTH"
                extractTitle={item => (item ? item.label : '')}
                placeholder="Select"
                selectedOption={genderOption}
                onSelect={this.updateGender}
                options={GENDER_INPUT_DEFAULTS}
                error={errors.gender}
              />
            </GenderInputWrapper>
            <InputWrapperMargin>
              <SelectInput
                label="PRONOUN"
                extractTitle={item => (item ? item.label : '')}
                placeholder="Select"
                selectedOption={pronounOption}
                onSelect={this.updatePronoun}
                options={PRONOUN_INPUT_DEFAULTS}
              />
            </InputWrapperMargin>
          </TwoFieldWrapper>
        </VMarginWrapper>

        <VMarginWrapper>
          <Query query={LABS}>
            {({ data }: LabsResult) => {
              // @ts-ignore
              this.sortedLabs = data?.labs.sort(this.alphabetically());
              return (
                <SelectInput
                  label="KINDBODY HOME LOCATION"
                  extractTitle={lab => (lab ? lab.name : '')}
                  placeholder={'Select city'}
                  selectedOption={patientLab}
                  onSelect={this.updatePatientLab}
                  options={(data && this.sortedLabs) || []}
                  error={errors.patientLab}
                />
              );
            }}
          </Query>
        </VMarginWrapper>

        <VMarginWrapper>
          <SelectWithOptions
            label="HOW DID YOU HEAR ABOUT US?"
            value={referralSource}
            placeholder={'Select one option'}
            lookup_key="patient_referral_source"
            onSelect={this.updateReferralSource}
          />
        </VMarginWrapper>

        <ButtonWrapper>
          <Button
            text="SUBMIT YOUR DETAILS"
            onClick={this._handleSignup.bind(this)}
            loading={this.props.loading}
          />
        </ButtonWrapper>
      </InputContainer>
    );
  }
}
